/**
 * The entitlement vocabulary: what a plan grants, and how much of it.
 *
 * Entitlements answer a different question from permissions. A permission asks "may THIS CALLER
 * perform this action?" and is decidable from the request alone, which is what makes route
 * middleware the right home for it. An entitlement asks "does THIS ORGANIZATION have access to
 * this resource, and how much?" — answerable only from domain state (a live seat count, the
 * payload being written, the row being touched), so it is asserted in the service that performs
 * the write, inside its transaction.
 *
 * A resource carries CONSTRAINTS, of three kinds:
 *   • a SET      — which variants are available: regions: ["us", "international"]
 *   • a QUANTITY — how much, with null meaning unlimited: max_recipients: 2000
 *   • a FLAG     — a capability with no variants and nothing to count: multi_group: true
 *
 * Prefer a SET wherever a feature could plausibly gain a sibling. Brand colours and confetti are
 * two members of `landing.customize` rather than two flags, so a fourth customization later is a
 * data change instead of a new key, a new assertion and a new hook. A flag is for the genuinely
 * binary: reach for it only when "which kind?" has no meaningful answer.
 */

/** A cap, or null for unlimited. */
export type Quantity = number | null;

/** A capability with no variants and nothing to count. */
export type Flag = boolean;

// ── Set members ─────────────────────────────────────────────────────────────────────────────
// Each is its own union so a typo is a compile error rather than a silently-false check.

export type Region = "us" | "international";
export type Scheduling = "asap" | "fixed" | "per_recipient";
export type Funding = "prepaid" | "auto_topup";
export type CollectionEdit = "reorder" | "add" | "remove";
export type LandingCustomization = "logo" | "banner" | "colors" | "confetti" | "video";
export type UnboxingCustomization = "message" | "skip" | "custom_video";
export type RedemptionLink = "email" | "manual" | "universal";
export type MerchAccess = "pod" | "hub" | "custom_kitting";
export type ContactAutomation = "birthday" | "anniversary";
export type ReportingFeature =
  | "campaign_basic"
  | "impact_basic"
  | "campaign_full"
  | "impact_full"
  | "analytics"
  | "esg_quarterly";
export type BillingTerm = "card" | "net";

/**
 * Every resource and its constraints. This is the whole vocabulary — a plan file declares one
 * value for each, and nothing outside this shape is an entitlement.
 *
 * Resources with no enforcement point yet (`redemption`, `merch`, `contacts`, `reporting`,
 * `billing`) are declared anyway: they are sold in the plan copy, declaring them costs nothing,
 * and it keeps the marketing list and the enforcement list a single list rather than two arrays
 * that drift.
 */
export interface EntitlementShape {
  campaign_send: {
    max_recipients: Quantity;
    regions: readonly Region[];
    scheduling: readonly Scheduling[];
  };
  campaign_budget: { funding: readonly Funding[] };
  /**
   * `max` is how many reminders a campaign may have; `custom_dates` is whether the sender may
   * override their auto-calculated send dates (Campaign Builder Step 2 and the post-launch
   * Notifications modal both gate on it). Independent knobs — a plan could in principle allow
   * reminders without letting the dates be hand-picked.
   */
  campaign_reminders: { max: Quantity; custom_dates: Flag };
  collection: { edit: readonly CollectionEdit[] };
  landing: { customize: readonly LandingCustomization[] };
  unboxing: { customize: readonly UnboxingCustomization[] };
  users: { max: Quantity };
  /**
   * Whether the client operates as more than one group. Below Pro Plus a client is a single
   * group and that group is invisible to them, so there is no wallet to choose between and no
   * second group to create.
   */
  wallet: { multi_group: Flag };
  redemption: { links: readonly RedemptionLink[] };
  merch: { access: readonly MerchAccess[] };
  contacts: { automation: readonly ContactAutomation[] };
  reporting: { features: readonly ReportingFeature[] };
  billing: { terms: readonly BillingTerm[] };
}

export type Resource = keyof EntitlementShape;

/** Constraints of `R` whose value is a set — the ones `allows()` accepts. */
export type SetConstraint<R extends Resource> = {
  [K in keyof EntitlementShape[R]]: EntitlementShape[R][K] extends readonly string[] ? K : never;
}[keyof EntitlementShape[R]];

/**
 * Constraints of `R` whose value is a quantity — the ones `limit()` / `within()` accept.
 *
 * `Flag` is excluded explicitly: `boolean` is not assignable to `number | null`, but stating it
 * keeps the three kinds mutually exclusive by construction rather than by coincidence.
 */
export type QuantityConstraint<R extends Resource> = {
  [K in keyof EntitlementShape[R]]: EntitlementShape[R][K] extends Flag
    ? never
    : EntitlementShape[R][K] extends Quantity
      ? K
      : never;
}[keyof EntitlementShape[R]];

/** Constraints of `R` whose value is a flag — the ones `enabled()` accepts. */
export type FlagConstraint<R extends Resource> = {
  [K in keyof EntitlementShape[R]]: EntitlementShape[R][K] extends Flag ? K : never;
}[keyof EntitlementShape[R]];

/** The members a given set constraint accepts, so `allows(…, "regions", "mars")` won't compile. */
export type SetMember<
  R extends Resource,
  C extends SetConstraint<R>,
> = EntitlementShape[R][C] extends readonly (infer M)[] ? M : never;

// ── Plans ───────────────────────────────────────────────────────────────────────────────────

/** Lowest to highest. The order is the RANK: plan cards and `org:plan` compare by index. */
export const PLAN_KEYS = ["starter", "pro", "pro_plus", "enterprise"] as const;

/** The plan vocabulary. The legacy `organizations.tier` value "free" maps to "starter". */
export type PlanKey = (typeof PLAN_KEYS)[number];

/**
 * The plans a client buys in the app, by card, through POST /tier/upgrade. Starter is free and
 * Enterprise is sold by GFG, so neither is here.
 */
export const SELF_SERVE_PLAN_KEYS = ["pro", "pro_plus"] as const satisfies readonly PlanKey[];

export type SelfServePlanKey = (typeof SELF_SERVE_PLAN_KEYS)[number];

export function isSelfServePlan(key: PlanKey | null | undefined): key is SelfServePlanKey {
  return (SELF_SERVE_PLAN_KEYS as readonly string[]).includes(key ?? "");
}

/**
 * How often a priced offer bills — Stripe's `recurring.interval`. A plan may be on sale at one
 * price per interval (Pro: monthly and annual); which intervals a plan is sold at is the
 * catalogue's rows, not code.
 */
export const BILLING_INTERVALS = ["month", "year"] as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export function isBillingInterval(v: string | null | undefined): v is BillingInterval {
  return (BILLING_INTERVALS as readonly string[]).includes(v ?? "");
}

/** A catalogue price: what an offer costs, or what an organization pays. */
export interface PlanPrice {
  interval: BillingInterval;
  /** Cents. 100000 = $1,000.00. */
  amount: number;
  /** Lower-case ISO code, as Stripe returns it. */
  currency: string;
}

/**
 * One line of a plan's feature list: a bullet, or a section heading such as
 * "Everything in Starter, plus".
 */
export type PlanFeature = string | { readonly divider: string };

/**
 * Everything the plan cards and upsell modals render for a plan. Held here rather than in the
 * frontend so a plan's price, name and feature list live beside the entitlements they describe,
 * and both are served by GET /plans from one place.
 */
export interface PlanDisplay {
  /** Full marketing name, e.g. "Good Pro". */
  name: string;
  /** Price figure as displayed, e.g. "$95", or "Custom pricing". */
  amountLabel: string;
  /** Suffix rendered immediately after the amount, e.g. "/month". Empty for Enterprise. */
  unit: string;
  /** Line beneath the price, e.g. "Billed annually at $1,140". */
  billingLabel: string;
  /** Short pill label for the summary bar and top-bar plan badge. */
  badgeLabel: string;
  /** The feature list, in render order. */
  features: readonly PlanFeature[];
}

/**
 * One plan at one policy version — the single source of truth for what that version grants and
 * how it is presented.
 *
 * `entitlements` is enforced; `display` is what the customer reads. Not every line sold in a plan
 * is machine-checkable ("Email and chat support", "Quarterly business review"), so the feature
 * list is copy rather than a derived view — but it sits in the same file as the constraints it
 * describes, and both are reviewed in the same change.
 *
 * A published version is APPEND-ONLY. Editing a version any organization is pinned to silently
 * changes what an existing customer is entitled to, which is the thing versioning exists to
 * prevent — a change is always a new version, never an edit.
 */
export interface PlanTemplate {
  key: PlanKey;
  version: number;
  label: string;
  entitlements: EntitlementShape;
  display: PlanDisplay;
}

/**
 * What `entitlementsFor(organizationId)` returns and what every assertion reads: the plan
 * identity plus the fully-resolved constraint values, with the policy version and any
 * per-organization overrides already collapsed in. Callers never see the inputs.
 */
export interface ResolvedEntitlements {
  plan: { key: PlanKey; version: number; label: string };
  entitlements: EntitlementShape;
}
